import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import CursorClickIcon from "@redotech/redo-icons/cursor-click-01.svg";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { PostPurchaseMobilePreview } from "../../../../conversion/post-purchase/post-purchase-mobile-preview";

/** Shows the post-purchase page attached to a checkout experience. */
export const CheckoutExperiencePpuHover = memo(
  function CheckoutExperiencePpuHover({
    checkoutExperienceId,
  }: {
    checkoutExperienceId: string | undefined;
  }) {
    const { t } = useTranslation();
    const conversionClient = useContext(RedoConversionRpcClientContext);
    const merchantClient = useContext(RedoMerchantRpcClientContext);

    const ppuLoad = useLoad(
      async (signal) => {
        if (!conversionClient || !merchantClient || !checkoutExperienceId) {
          return undefined;
        }
        const experience = await conversionClient.getCheckoutExperienceById(
          { checkoutExperienceId },
          { signal },
        );
        const upsellPageId = experience.upsellPageId?.toString();
        if (!upsellPageId) {
          return { kind: "none" as const };
        }
        const page = await merchantClient.getUpsellPage({ upsellPageId });
        if (!page) {
          return { kind: "none" as const };
        }
        return {
          kind: "page" as const,
          upsellPageData: page.upsellPageData,
          name: page.name ?? null,
        };
      },
      [conversionClient, merchantClient, checkoutExperienceId],
    );

    if (ppuLoad.pending && ppuLoad.value === undefined) {
      return (
        <Text fontSize="xs" textColor="secondary">
          {t("Loading preview…")}
        </Text>
      );
    }

    const value = ppuLoad.value;
    if (!value || value.kind === "none") {
      return <PpuHoverEmpty label={t("No upsell page attached")} />;
    }

    return (
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="semibold" textColor="primary">
          {value.name ?? t("Untitled page")}
        </Text>
        <PostPurchaseMobilePreview upsellPageData={value.upsellPageData} />
      </Flex>
    );
  },
);

const PpuHoverEmpty = memo(function PpuHoverEmpty({
  label,
}: {
  label: string;
}) {
  return (
    <Flex align="center" dir="column" gap="xs" p="sm">
      <RedoFeaturedIcon color="gray" Icon={CursorClickIcon} size="sm" />
      <Text fontSize="xs" textColor="secondary">
        {label}
      </Text>
    </Flex>
  );
});
